import { AuthApi, Configuration, DomainsApi, UsersApi } from './openapi';

import type { AxiosInstance } from 'axios';
import type {
  CloudUserInfo,
  FetchDomainDetailInfoReqParams,
  FetchProjectsReqParams,
  FetchUserDetailInfoReqParams,
  UpdateUserDetailInfoReqParams,
} from '../../constants';
import type {
  AuthApiApiV1AuthTokensIssuePostRequest,
  UsersApiApiV11UsersUserIdProjectsGetRequest,
} from './openapi';

export type FetchCloudUserInfoProps = Omit<AuthApiApiV1AuthTokensIssuePostRequest, 'xAuthToken'> & {
  clientIp?: string;
};

export type FetchProjectsReqProps = Omit<UsersApiApiV11UsersUserIdProjectsGetRequest, 'xAuthToken'>;

const timeout = 25 * 1000;

type IamApiInstanceReqParams = {
  axiosInstance: AxiosInstance;
  iamEndpoint: string;
  token: string;
};

export const getIamApiFetchers = (params: IamApiInstanceReqParams) => {
  const { axiosInstance, token, iamEndpoint } = params;

  const configuration = new Configuration({
    baseOptions: {
      timeout,
    },
  });
  const authApi = new AuthApi(configuration, iamEndpoint, axiosInstance);
  const usersApi = new UsersApi(configuration, iamEndpoint, axiosInstance);
  const domainsApi = new DomainsApi(configuration, iamEndpoint, axiosInstance);

  /**
   * /api/v1/auth/tokens/issue
   * @param reqParams query : 사용자가 향할 목적지 (project or domain)
   * @returns 토큰을 갱신하는 데 성공한 scope 의 사용자 정보
   */
  const fetchCloudUserInfo = async (reqParams: FetchCloudUserInfoProps) => {
    const { clientIp, ...others } = reqParams;

    const { data, headers } = await authApi.apiV1AuthTokensIssuePost(
      {
        xAuthToken: token,
        ...others,
      },
      {
        params: {
          client_ip: clientIp,
        },
      },
    );

    const refinedData = { ...data, authToken: headers['x-subject-token'] } as CloudUserInfo;

    return refinedData;
  };

  /**
   * /api/v1_2/users/{user_id}/projects
   * @param reqParams baseURL : 환경 별 iam endpoint / token : project scope / userId : 사용자 uuid
   * @returns 현재 사용자가 접근 가능한 역할의 프로젝트 정보
   */
  const fetchProjects = async (reqParams: FetchProjectsReqParams) => {
    const { data } = await usersApi.apiV12UsersUserIdProjectsGet({
      xAuthToken: token,
      size: 0,
      ...reqParams,
    });

    return data.projects || [];
  };

  /**
   * /api/v1/users/{user_id}
   * @param reqParams userId: 특정 사용자의 고유 아이디를 전달하여 상세 정보 조회
   * @returns 고유 아이디에 해당하는 사용자 상세 정보
   */
  const fetchUserDetailInfo = async (reqParams: FetchUserDetailInfoReqParams) => {
    const { data } = await usersApi.apiV1UsersUserIdGet({ xAuthToken: token, ...reqParams });

    return data;
  };

  /**
   * /api/v1_1/domains/{domain_id}
   * @params reqParams domainId: 특정 도메인의 고유 아이디를 전달하여 상세 정보 조회
   * @returns 고유 아이디에 해당하는 도메인 상세 정보
   */
  const fetchDomainDetailInfo = async (reqParams: FetchDomainDetailInfoReqParams) => {
    const { data } = await domainsApi.apiV11DomainsDomainIdGet({ xAuthToken: token, ...reqParams });

    return data;
  };

  const updateUserDetailInfo = async (reqParams: UpdateUserDetailInfoReqParams) => {
    const { data } = await usersApi.apiV1UsersUserIdPatch({ xAuthToken: token, ...reqParams });
    return data;
  };

  /**
   * /api/v1/auth/check-password
   * @returns 비밀번호 재확인 성공 여부 조회
   * 204: 비밀번호 재확인 성공
   * 404: 비밀번호 재확인 성공 기록 없음
   */
  const headAuthCheckPassword = async () => {
    const { status } = await authApi.apiV1AuthCheckPasswordHead({ xAuthToken: token });
    return status;
  };

  return {
    fetchCloudUserInfo,
    fetchProjects,
    fetchUserDetailInfo,
    fetchDomainDetailInfo,
    updateUserDetailInfo,
    headAuthCheckPassword,
  };
};
